import { generateChartConfig, generateStackedConfig } from '@ctn/ui/components/ctn-chart/utils/generator';
import { type Chart, type ChartData } from 'chart.js';

import { type EstadisticaResponse } from '@/services/tramite.service';

type ColorMap = Record<number, string>;

const DEFAULT_COLOR = '#9e9e9e';

export type SubGroupData = {
	valor: number;
	id: number;
};

export type ProcessedGroup = {
	label: string;
	total: number;
	data: Record<string, SubGroupData>;
};

type StackedBarListItem = {
	label: string;
	total: number;
	data: ChartData<'bar'>;
};

type GroupData = {
	total: number;
	id: number;
};

/**
 * Prepara los datos para gráficos simples.
 * * @description
 * Reduce la lista de tipos/estados a un formato plano, sumando totales por la 
 * dimensión elegida, ordenándolos de mayor a menor y asignando colores 
 * específicos mediante un mapa de IDs.
 */
export const processSolicitudesData = (
	response: EstadisticaResponse,
	groupBy: 'tipo' | 'estado',
	colorMap: ColorMap = {},
) => {
	const grouped = response.listaTipoYEstado.reduce<Record<string, GroupData>>((acc, item) => {
		const key = item[groupBy].descripcion;
		const { id } = item[groupBy];

		if (!acc[key]) {
			acc[key] = { total: 0, id };
		}

		acc[key].total += item.total;

		return acc;
	}, {});

	const sortedEntries = Object.entries(grouped).sort(([, dataA], [, dataB]) => dataB.total - dataA.total);

	return {
		labels: sortedEntries.map(([key]) => key),

		values: sortedEntries.map(([, data]) => data.total),

		colors: sortedEntries.map(([, data]) => colorMap[data.id] || DEFAULT_COLOR),

		total: response.total,
	};
};

/**
 * Agrupa y totaliza estadísticas por dos dimensiones (tipo y estado).
 * * @description
 * Organiza una lista plana de datos en un mapa de grupos principales y subgrupos.
 * Si la clave primaria es 'tipo', agrupa por tipo y desglosa cada uno por sus estados.
 */
export const groupDataBy = (
	response: EstadisticaResponse,
	primaryKey: 'tipo' | 'estado' = 'tipo',
): ProcessedGroup[] => {
	const secondaryKey = primaryKey === 'tipo' ? 'estado' : 'tipo';

	const map = response.listaTipoYEstado.reduce<Record<string, ProcessedGroup>>((acc, item) => {
		const mainLabel = item[primaryKey].descripcion;
		const subLabel = item[secondaryKey].descripcion;
		const subId = item[secondaryKey].id;

		if (!acc[mainLabel]) {
			acc[mainLabel] = {
				label: mainLabel,
				total: 0,
				data: {},
			};
		}

		const group = acc[mainLabel];

		if (!group.data[subLabel]) {
			group.data[subLabel] = { valor: 0, id: subId };
		}

		const incremento = Number(item.total) || 0;

		group.data[subLabel].valor += incremento;
		group.total += incremento;

		return acc;
	}, {});

	return Object.values(map).sort((a, b) => b.total - a.total);
};


export const getStackedBarList = ({
	data,
	colorMap,
	groupBy,
}: {
	data: EstadisticaResponse;
	colorMap: Record<number, string>;
	groupBy: 'tipo' | 'estado';
}): StackedBarListItem[] => {

	const groups = groupDataBy(data, groupBy);

	return groups.map((group) => ({
		label: group.label,
		total: group.total,
		data: {
			labels: [group.label],
			datasets: Object.entries(group.data).map(([estadoNombre, info]) => ({
				label: estadoNombre,
				data: [info.valor],
				backgroundColor: colorMap[info.id] || '#9e9e9e',
			})),
		},
	}));
};

export const getDoughnutData = ({
	data,
	groupBy,
	colorMap,
}: {
	data: EstadisticaResponse;
	groupBy: 'tipo' | 'estado';
	colorMap: ColorMap;
}): ChartData<'doughnut'> => {
	const { labels, values, colors } = processSolicitudesData(data, groupBy, colorMap);

	return {
		labels,
		datasets: [
			{
				data: values,
				backgroundColor: colors,
				borderWidth: 2,
				borderColor: '#ffffff',
			},
		],
	};
};

/** Versión modificada de generateStackedConfig para ajustarse a los estilos de PNP */
export const customGenerateStackedConfig = (data: ChartData<'bar'>, total: number) => {
	const { datasets = [] } = data;
	const config = generateStackedConfig(data, {
		orientation: 'y',
		animation: false,
		displayAxis: false,
		displayGrid: false,
		displayTicksX: false,
		displayTicksY: false,
		responsive: true,
		maintainAspectRatio: false,
		tooltip: 'custom',
		tooltipCustomRender(_, value, index) {
			return `${datasets[index].label}: ${value}`;
		},
	});

	return {
		...config,
		options: {
			...config.options,
			responsive: true,
			layout: {
				padding: 0,
			},
			barThickness: 32,
			borderWidth: 1,
			borderColor: '#ffffff',
			scales: {
				...config.options?.scales,
				x: { ...config.options?.scales?.x, max: total },
			},
		},
		plugins: [],
	};
};

/**
 * Plugin para Chart.js que dibuja el total acumulado en el centro de un Doughnut.
 * * @description
 * Calcula la suma de todos los datasets para mostrar el total central. 
 * Si se proporciona `totalExternal`, se prioriza ese valor sobre el cálculo
 */
const centerTotalPlugin = ({ totalExternal }: { totalExternal?: number }) => ({
	id: 'centerTotal',
	beforeDraw(chart: Chart<'doughnut'>) {
		const { width, height, ctx, config } = chart;
		let total = 0;

		if (totalExternal === undefined) {
			total = config.data.datasets.reduce((accDataset, dataset) => {
				const sumaDataset = dataset.data.reduce((a: number, b: number) => a + (b || 0), 0);
				return accDataset + sumaDataset;
			}, 0);
		} else {
			total = totalExternal;
		}

		ctx.restore();

		ctx.font = '400 22px "Open Sans", sans-serif';
		ctx.textBaseline = 'middle';
		ctx.fillStyle = '#263240';

		const text = total.toString();
		const textX = Math.round((width - ctx.measureText(text).width) / 2);
		const textY = height / 2;

		ctx.fillText(text, textX, textY);
		ctx.save();
	},
});

/** Versión modificada de generateChartConfig para ajustarse al diseño de PNP */
export const customGenerateDoughnutConfig = (data: ChartData<'doughnut'>, hasData: boolean) => {
	const config = generateChartConfig('doughnut', data, {
		orientation: 'y',
		animation: true,
		displayAxis: false,
		displayGrid: false,
		displayTicksX: false,
		displayTicksY: false,
		responsive: true,
		maintainAspectRatio: false,
		tooltip: hasData ? 'custom' : 'none',
		tooltipCustomRender: (label, value) => `${label} (${value})`,
	});

	return {
		...config,
		interaction: {
			mode: 'nearest',
		},
		options: {
			...config.options,
			cutout: '65%',
		},
		plugins: [centerTotalPlugin({ totalExternal: hasData ? undefined : 0 })],
	};
};
